import { OebbAutocomplete } from "@oebb-web-team/oebb-web-library/dist/autocomplete/OebbAutocomplete";
import { h } from "@stencil/core";
import { TrainService } from "../../service/train.service";
import { findLocale, formatIsoDate, getLocalizedString, i18n } from "../../utils";
import { connectionsI18n } from "./connections-i18n";
export class Connections {
    constructor() {
        this.vehicleTransport = false;
        this.showBestPriceFinder = true;
        this.showAutoTrains = true;
        this.today = new Date();
        this.travelDate = formatIsoDate(this.today);
        this.direction = 'outward';
        this.onCountryBlur = () => this.refreshConnections();
        this.onDirectionSelect = (e) => {
            const target = e.target;
            this.direction = target.value;
            this.updateSearchData();
            this.refreshConnections().then();
        };
        this.onDateChange = (e) => {
            this.travelDate = formatIsoDate(e.detail.value);
            this.updateSearchData();
            this.refreshConnections().then();
        };
        this.onExpandableOpened = (e) => {
            this.activeTrain = e.detail ? e.currentTarget.dataset.train : null;
        };
    }
    componentWillLoad() {
        this.lang = findLocale(this.el);
        i18n.add(connectionsI18n);
        i18n.setLang(this.lang);
        if (this.njBaseUrl) {
            TrainService.instance.setNjBaseUrl(this.njBaseUrl);
        }
        if (this.pvAppsBaseUrl) {
            TrainService.instance.setPvAppsBaseUrl(this.pvAppsBaseUrl);
        }
        if (this.type) {
            this.travelDate = formatIsoDate(new Date());
            this.searchData = {
                carTransport: this.vehicleTransport,
                date: this.travelDate,
            };
        }
        this.initConfig().then();
        this.refreshConnections().then();
    }
    componentDidLoad() {
        this.initAutocomplete().then();
    }
    async initConfig() {
        try {
            await TrainService.instance.loadConfig();
        }
        catch (e) {
            console.log("Error loading config", e);
        }
        finally {
            // Set the date limit even when the request failed, at least we enforce the 180 day limit
            this.maxBookableDate = TrainService.instance.getMaxBookableDate();
        }
    }
    async refreshConnections() {
        var _a;
        try {
            if (this.type) {
                this.data = await TrainService.instance.findConnectionsForType(this.type, this.travelDate || formatIsoDate(new Date()), this.vehicleTransport);
            }
            else if (!this.originCountry || !this.travelDate) {
                this.data = [];
            }
            else if (this.destinationCity) {
                this.data = await TrainService.instance.findConnectionsForDestinationStation(this.originCountry, this.destinationCity, this.travelDate, this.vehicleTransport, this.direction);
            }
            else {
                this.data = await TrainService.instance.findConnectionsForDestinationCountry(this.originCountry, this.destinationCountry, this.travelDate, this.vehicleTransport, this.direction);
            }
            if (((_a = this.data) === null || _a === void 0 ? void 0 : _a.length) === 1) {
                this.activeTrain = this.data[0].trainNumber;
            }
        }
        catch (e) {
            console.log("Error loading connections", e);
        }
    }
    async findAvailableCountries(query = '') {
        try {
            if (this.destinationCity) {
                return await TrainService.instance.findCountriesForDestinationStation(this.destinationCity, this.vehicleTransport, this.direction, query);
            }
            else {
                return await TrainService.instance.findCountriesForDestinationCountry(this.destinationCountry, this.vehicleTransport, this.direction, query);
            }
        }
        catch (e) {
            console.log('Error loading country list', e);
            return [];
        }
    }
    async initAutocomplete() {
        if (this.type) {
            return;
        }
        const countryInput = this.el.shadowRoot.querySelector('#country-field');
        this.countryAutocomplete = new OebbAutocomplete(countryInput)
            .withTemplate(function (item) {
            return `<li class="oebb-autocomplete-item js-oebb-autocomplete-item">${getLocalizedString(item.name)}</li>`;
        })
            .onItemActive((index) => this.selectCountry(index))
            .onItemSelect((index, e) => {
            e.preventDefault();
            this.selectCountry(index);
            this.countryAutocomplete.hideList();
            this.refreshConnections().then();
        })
            .onKeyup(async () => {
            const query = this.countryAutocomplete.getValue();
            const countries = await this.findAvailableCountries(query === null || query === void 0 ? void 0 : query.trim());
            this.countryAutocomplete.setData(countries);
            this.countryAutocomplete.render();
        })
            .withPanelOnFocus();
        // Load the country list and user country, but do it asynchronously
        const [userCountryIso, countries] = await Promise.all([
            this.obtainLocation(),
            this.findAvailableCountries(),
        ]);
        this.countryAutocomplete.setData(countries);
        if (userCountryIso) {
            const userCountry = countries.find(country => country.iso === userCountryIso);
            if (userCountry) {
                this.countryAutocomplete.setValue(getLocalizedString(userCountry.name));
                this.originCountry = userCountry.eva;
                this.updateSearchData();
                this.refreshConnections().then();
            }
        }
    }
    selectCountry(index) {
        const country = this.countryAutocomplete.getData()[index];
        this.countryAutocomplete.setValue(getLocalizedString(country === null || country === void 0 ? void 0 : country.name));
        this.originCountry = country === null || country === void 0 ? void 0 : country.eva;
        this.updateSearchData();
    }
    obtainLocation() {
        if (this.departureCountryCode !== undefined && this.departureCountryCode !== null) {
            return new Promise((resolve) => {
                resolve(this.departureCountryCode);
            });
        }
        else {
            return TrainService.instance.getGeolocation();
        }
    }
    updateSearchData() {
        this.searchData = this.direction === 'return' ? {
            fromCountry: this.destinationCountry,
            fromStation: this.destinationCity,
            toCountry: this.originCountry,
            carTransport: this.vehicleTransport,
            date: this.travelDate,
        } : {
            fromCountry: this.originCountry,
            toCountry: this.destinationCountry,
            toStation: this.destinationCity,
            carTransport: this.vehicleTransport,
            date: this.travelDate,
        };
    }
    renderForm() {
        if (this.type) {
            // Do not show form for partner train view
            return null;
        }
        return (h("form", { class: "connections-form" }, h("fieldset", null, h("legend", { class: "hidden" }, i18n.get('connections.form.header')), h("div", { class: "connections-form-grid" }, h("label", { htmlFor: "country-field", id: "country-label", class: "connections-form-label" }, h("span", null, i18n.get('connections.form.from'))), h("oebbx-input-field", { type: "text", mode: "2021", htmlId: "country-field", class: "connections-form-country", autocomplete: "off", onBlur: this.onCountryBlur }), h("div", { class: "connections-form-direction" }, ['outward', 'return'].map(direction => (h("label", { class: "connections-form-radio-label" }, h("input", { type: "radio", name: "direction", class: "connections-form-radio", value: direction, checked: this.direction === direction, onInput: this.onDirectionSelect }), h("span", null, i18n.get(`connections.form.${direction}`)))))), h("label", { htmlFor: "date-field", class: "connections-form-label" }, h("span", null, i18n.get('connections.form.date'))), h("oebbx-date-field", { "html-id": "date-field", mode: "2021", class: "connections-form-date", label: i18n.get('connections.form.date'), disabledBefore: this.today, disabledAfter: this.maxBookableDate, startDate: this.today, onFormDataChanged: this.onDateChange })), h("button", { class: "screen-reader-text", disabled: true }, "$", i18n.get('connections.form.search')))));
    }
    renderConnectionItem(connection) {
        var _a, _b;
        const isOpen = connection.trainNumber === this.activeTrain;
        return (h("oebbx-expandable-item", { isOpen: isOpen, "data-train": connection.trainNumber, onExpandableOpened: this.onExpandableOpened }, h("div", { slot: "header", class: "connection-header" }, h("span", { class: "connection-number" }, connection.trainNumber), h("span", { class: "connection-title" }, getLocalizedString((_a = connection.stationFrom) === null || _a === void 0 ? void 0 : _a.name), "\u00A0\u00A0-\u00A0\u00A0", getLocalizedString((_b = connection.stationTo) === null || _b === void 0 ? void 0 : _b.name)), h("span", { class: connection.carTransport ? 'connection-vehicle-possible' : 'connection-vehicle-not-possible' }, this.showAutoTrains
            ? [
                h("oebbx-icon", { name: "cartrain", "aria-hidden": "true" }),
                h("span", null, connection.carTransport
                    ? i18n.get("connections.cartransport.possible", "Fahrzeugtransport möglich")
                    : i18n.get("connections.cartransport.notpossible", "Fahrzeugtransport nicht möglich"))
            ]
            : null)), h("oebbx-train-details", { connection: connection, searchData: this.searchData, njBookingLink: this.njBookingLink, partnerLink: this.partnerLink, showBestPriceFinder: this.showBestPriceFinder, showAutoTrains: this.showAutoTrains, isOpen: isOpen })));
    }
    renderConnections() {
        var _a;
        if (!((_a = this.data) === null || _a === void 0 ? void 0 : _a.length)) {
            return (h("div", { class: "connection-none" }, h("p", null, i18n.get('connections.none'))));
        }
        const oebbTrains = this.data.filter(train => !train.partnerTrain);
        const partnerTrains = this.data.filter(train => train.partnerTrain);
        return (h("div", { class: "connection-results" }, h("div", { class: "screen-reader-text", "aria-live": "polite" }, i18n.get(`connections.results.${this.data.length === 1 ? 'single' : 'multiple'}`, null, this.data.length.toString())), oebbTrains.length ? (h("section", null, h("h3", { class: "connection-label screen-reader-text", id: "connections-oebb" }, i18n.get(this.type === 'dani' ? 'connections.dani' : 'connections.oebb')), h("oebbx-expandable-list", { "aria-labelledby": "connections-oebb" }, oebbTrains.map(item => this.renderConnectionItem(item))))) : null, partnerTrains.length ? (h("section", null, h("h3", { class: "connection-label", id: "connections-partner" }, i18n.get('connections.partner')), h("oebbx-expandable-list", { "aria-labelledby": "connections-partner" }, partnerTrains.map(item => this.renderConnectionItem(item))))) : null));
    }
    render() {
        return (h("div", { key: 'a2b82203cd8348a6ab18eff3c28a8c396bf501ba' }, this.renderForm(), this.renderConnections()));
    }
    static get is() { return "oebbx-app-connections"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["connections.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["connections.css"]
        };
    }
    static get properties() {
        return {
            "njBookingLink": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "nj-booking-link",
                "reflect": false
            },
            "partnerLink": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "partner-link",
                "reflect": false
            },
            "destinationCountry": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "destination-country",
                "reflect": false
            },
            "destinationCity": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "destination-city",
                "reflect": false
            },
            "vehicleTransport": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "vehicle-transport",
                "reflect": false,
                "defaultValue": "false"
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ConnectionsType",
                    "resolved": "\"dani\" | \"partner\"",
                    "references": {
                        "ConnectionsType": {
                            "location": "import",
                            "path": "../../models/nightjet",
                            "id": "src/models/nightjet.ts::ConnectionsType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "type",
                "reflect": false
            },
            "njBaseUrl": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "nj-base-url",
                "reflect": false
            },
            "pvAppsBaseUrl": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "pv-apps-base-url",
                "reflect": false
            },
            "showBestPriceFinder": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "show-best-price-finder",
                "reflect": false,
                "defaultValue": "true"
            },
            "showAutoTrains": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "show-auto-trains",
                "reflect": false,
                "defaultValue": "true"
            },
            "departureCountryCode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "departure-country-code",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "data": {},
            "maxBookableDate": {},
            "activeTrain": {}
        };
    }
    static get elementRef() { return "el"; }
}
//# sourceMappingURL=connections.js.map
